import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { Feature } from '@loyalty/shared-common';
import { QlfRte } from '@qualifio/ui-commons/dist/components/qlf-rte';
import { TextField, Theme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { useTranslation } from 'react-i18next';
import { LanguageTabs } from '../../../shared/language-tabs';
import { UploadableImage } from '../../../shared/image';
import { onQlfRteChange } from '../../../shared/forms/on-qlf-rte-change';
import { useFeatures } from '../../../features/FeaturesContext';
import { useProgram } from '../../../loyalty/ProgramContext';

const useStyles = makeStyles<Theme>(({ spacing }) => {
  return {
    root: {},
    description: {
      display: 'flex',
    },
    text: {
      display: 'flex',
      flexDirection: 'column',
      flex: 1,
      padding: spacing(0, 0, 0, 2),
    },
  };
});

export function LevelI18nAttributesForm(): React.ReactElement {
  const classes = useStyles();
  const { t } = useTranslation();
  const { program } = useProgram();
  const { features } = useFeatures();
  const { write: levelWrite } = features[Feature.LEVEL];
  const {
    formState: { errors },
    setError,
    clearErrors,
    control,
    getValues,
  } = useFormContext();

  const programId = program.id;

  const handleUploadError = (name: string, message: string | undefined) => {
    if (message) {
      setError(name, { message });
    } else {
      clearErrors(name);
    }
  };

  return (
    <div className={classes.root}>
      <LanguageTabs
        languages={program.languages}
        message={t('levels.edition.message')}
        errors={errors?.i18nAttributes}
      >
        {({ language }) => {
          return (
            <div key={language} className={classes.description}>
              <Controller
                control={control}
                name={`i18nAttributes.${language}.image`}
                defaultValue=""
                render={({ field: { ref, ...field }, fieldState: { error } }) => (
                  <UploadableImage
                    {...field}
                    disabled={!levelWrite}
                    programId={programId}
                    type="reward"
                    errorMessage={error?.message}
                    onError={handleUploadError}
                  />
                )}
              />
              <div className={classes.text}>
                <Controller
                  control={control}
                  name={`i18nAttributes.${language}.name`}
                  defaultValue=""
                  render={({ field: { ref, ...field }, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      disabled={!levelWrite}
                      inputRef={ref}
                      id="level-name-id"
                      placeholder={t('levels.edition.placeholders.name')}
                      error={!!error}
                      helperText={error?.message}
                    />
                  )}
                />
                <Controller
                  control={control}
                  name={`i18nAttributes.${language}.description`}
                  defaultValue=""
                  render={({ field: { ref, ...field }, fieldState: { error } }) => (
                    <QlfRte
                      {...field}
                      onChange={onQlfRteChange(
                        field.onChange,
                        () => getValues().i18nAttributes?.[language]?.description,
                      )}
                      disabled={!levelWrite}
                      inputRef={ref}
                      error={!!error}
                      helperText={error?.message}
                      label={t('levels.edition.placeholders.description')}
                      rows={5}
                      multiline
                    />
                  )}
                />
              </div>
            </div>
          );
        }}
      </LanguageTabs>
    </div>
  );
}
